import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useCollection } from '../hooks/useCollection';
import { useProductPrices } from '../hooks/useProductPrices';
import { ProductCard, ViewMoreCard } from './product/ProductCard';
import { FeaturedSectionSkeleton } from './ui/Skeleton';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import { Navigation } from 'swiper/modules';
import '../styles/featured-products.css';

interface CollectionProduct {
  id: string;
  name: string;
  main_image: string;
  price?: number;
  sales?: number;
  stock?: number;
  freeShipping?: boolean;
  minOrder?: number;
  code?: string;
}

export default function FeaturedProducts() {
  const { collection, loading: collectionLoading, error } = useCollection('featured');

  // Obtener IDs de productos para el hook de precios
  const productIds = useMemo(() => {
    if (!collection?.products?.length) return [];
    return collection.products.slice(0, 8).map(p => p.id);
  }, [collection?.products]);

  // Usar el hook centralizado de precios
  const { prices, loading: pricesLoading } = useProductPrices(productIds, {
    enabled: productIds.length > 0
  });

  const loading = collectionLoading || pricesLoading;

  if (loading) {
    return <FeaturedSectionSkeleton count={3} />;
  }

  if (error) {
    return (
      <section>
        <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg">
          Error: {error}
        </div>
      </section>
    );
  }

  if (!collection || !collection.products?.length) {
    return null;
  }

  return (
    <section className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">{collection.name}</h2>
          <p className="text-gray-600">{collection.description}</p>
        </div>
      </div>

      <Swiper
        modules={[Navigation]}
        navigation
        spaceBetween={24}
        slidesPerView={1}
        breakpoints={{
          640: { slidesPerView: 2 },
          768: { slidesPerView: 3 },
          1024: { slidesPerView: 4 },
        }}
        className="featured-products-slider"
      >
        {collection.products.slice(0, 8).map((product: CollectionProduct) => {
          const priceData = prices[product.id];

          return (
            <SwiperSlide key={product.id}>
              <ProductCard
                product={{
                  id: product.id,
                  name: product.name,
                  main_image: product.main_image,
                  min_order: product.minOrder || 12,
                  code: product.code,
                  stock: product.stock
                }}
                priceInfo={priceData ? {
                  price: priceData.isOnSale && priceData.offerPrice ? priceData.offerPrice : priceData.price,
                  originalPrice: priceData.isOnSale ? priceData.price : null,
                  isOnSale: priceData.isOnSale,
                  offerDays: priceData.offerDays,
                  discountPercentage: priceData.discountPercentage
                } : undefined}
                variant="featured"
                showCode={true}
                showQuickBuy={true}
                showMinOrder={false}
              />
            </SwiperSlide>
          );
        })}

        {collection.products.length > 8 && (
          <SwiperSlide>
            <ViewMoreCard />
          </SwiperSlide>
        )}
      </Swiper>
    </section>
  );
}
